import { Component, signal } from '@angular/core';
import { AngularModule } from '../../../modules/angular.module';
import { ZorroModule } from '../../../modules/zorro.module';
import { NavigationService } from '../../../services/navigation.service';
import { ClientService } from '../../../services/client.service';
import { combineLatest, Subscription } from 'rxjs';
import { Activity, Client, Course, Engines, Lesson, Product, Section, VerifyUuidsPayload, _ } from '@mira-avant/mtlibs';
import { CarouselComponent } from '../../ui/journey/carousel/carousel.component';
import { PlayerComponent } from '../../ui/journey/player/player.component';
import { HeaderComponent } from '../../ui/journey/header/header.component';
import { CourseComponent } from '../../ui/journey/course/course.component';
import { PageBoxComponent } from '../../ui/journey/page-box/page-box.component';
import { SectionService } from '../../../services/section.service';
import { ActivityService } from '../../../services/activity.service';
import { LessonService } from '../../../services/lesson.service';
import { UserService } from '../../../services/user.service';
import { CourseService } from '../../../services/course.service';
import { Security } from '../../../services/security';
import { NzMessageService } from 'ng-zorro-antd/message';

export enum JourneyStep {
	CHOOSE_COURSE_AND_UNIT_AND_LESSON = 'choose-course-and-unit-and-lesson',
	CHOOSE_ACTIVITY = 'choose-activity',
	PLAY_ACTIVITY = 'play-activity',
}

@Component({
	selector: 'app-journey',
	standalone: true,
	imports: [
		AngularModule,
		ZorroModule,
		CarouselComponent,
		PlayerComponent,
		HeaderComponent,
		CourseComponent,
		PageBoxComponent,
	],
	templateUrl: './journey.component.html',
	styleUrl: './journey.component.scss',
})
export class JourneyComponent {
	public client = signal<Client | undefined>(undefined);
	public step = signal<JourneyStep>(JourneyStep.CHOOSE_COURSE_AND_UNIT_AND_LESSON);
	public JourneyStep = JourneyStep;
	public coursesMatchingSection = signal<Course[]>([]);
	public activity = signal<Activity | undefined>(undefined);
	public lesson = signal<Lesson | undefined>(undefined);
	public enablePrevious = signal<boolean>(false);
	public enableNext = signal<boolean>(false);
	public isStrideCourse = signal<boolean | undefined>(undefined);
	private course = signal<Course | undefined>(undefined);
	private sub: Subscription | undefined;

	constructor(
		public nav: NavigationService,
		public clientService: ClientService,
		private courseService: CourseService,
		private sectionService: SectionService,
		public activityService: ActivityService,
		private lessonService: LessonService,
		private userService: UserService,
		private securityService: Security,
		private message: NzMessageService
	) {
		this.courseService
			.getCourseByUrl()
			.then(async course => {
				this.course.set(course);

				if (course) {
					const courseProduct: Product = await this.courseService.getProduct$(course.uuid);
					this.isStrideCourse.set(courseProduct.engine_name === Engines.STRIDE);
				} else {
					this.isStrideCourse.set(false);
				}
			})
			.catch(error => {
				console.error('Error fetching course:', error);
				this.isStrideCourse.set(false);
			});
	}

	async ngOnInit() {
		this.sub = this.nav.matrixParams$.subscribe(async (params: Record<string, string>) => {
			const paramCount = Object.keys(params).length;

			if (paramCount > 0) {
				await this.securityService.verify$(params as unknown as VerifyUuidsPayload).then(async hasAccess => {
					if (!hasAccess) {
						this.message.error('Configuration mis-match, please select your class again.');
						this.nav.navigateWithMatrix$(['/', 'my', 'journey'], {}, false);
						return;
					}
				});
			}

			//Only set the activity if it has changed
			if (this.activity()?.uuid !== params['activityUuid']) {
				this.activity.set(undefined);
				this.activity.set(
					params['activityUuid'] ? await this.activityService.get$(params['activityUuid'], true) : undefined
				);
			}

			//Only set the lesson if it has changed
			if (this.lesson()?.uuid !== params['lessonUuid']) {
				this.lesson.set(undefined);
				this.lesson.set(
					params['lessonUuid'] ? await this.lessonService.get$(params['lessonUuid'], true) : undefined
				);
			}

			//Look over this.lesson.activity and compare against our this.activity.uuid and see
			//if we can enable
			// the previous and next buttons
			const lesson = this.lesson();
			if (!lesson || !lesson.activity) {
				return;
			}

			lesson.activity.forEach((activity: Activity, i: number) => {
				if (!lesson) {
					throw new Error('Lesson not found');
				}

				if (!lesson.activity || lesson.activity.length === 0) {
					throw new Error('Lesson has no activities');
				}

				if (activity.uuid === this.activity()?.uuid) {
					this.enablePrevious.set(i > 0);
					this.enableNext.set(i < lesson.activity.length - 1);
				}
			});
		});

		this.sub.add(
			combineLatest([this.nav.matrixParams$, this.clientService.selectedClient$]).subscribe(
				async ([params, client]: [any, Client]) => {
					this.client.set(client);

					const section: Section | undefined = params.sectionUuid
						? await this.sectionService.get$(params.sectionUuid)
						: undefined;

					this.coursesMatchingSection.set([]);

					if (!section) {
						console.warn('No section found');
						return;
					}

					this.client()?.course?.forEach((course: Course) => {
						if (section.course_uuid !== course.uuid) {
							return;
						}

						this.coursesMatchingSection.update(list => [...list, course]);
					});
				}
			)
		);

		this.sub.add(
			this.nav.matrixParams$.subscribe(async (matrixParams: { [key: string]: string }) => {
				this.updateSteyByRoute(matrixParams);
			})
		);
	}

	public async onPrevious(): Promise<void> {
		if (!this.lesson() || !this.lesson()?.activity) {
			throw new Error('Lesson not found');
		}

		const lesson = this.lesson();

		if (!lesson || !lesson.activity) {
			throw new Error('Lesson not found');
		}

		lesson.activity.forEach((activity: Activity, i: number) => {
			if (!lesson) {
				throw new Error('Lesson not found');
			}

			if (!lesson.activity || lesson.activity.length === 0) {
				throw new Error('Lesson has no activities');
			}

			if (activity.uuid === this.activity()?.uuid) {
				if (i > 0) {
					this.nav.navigateWithMatrix$(['/', 'my', 'journey'], {
						activityUuid: lesson.activity[i - 1].uuid,
					});
				}
			}
		});
	}

	public async onNext(): Promise<void> {
		if (!this.lesson() || !this.lesson()?.activity) {
			throw new Error('Lesson not found');
		}
		const lesson = this.lesson();

		if (!lesson || !lesson.activity) {
			throw new Error('Lesson not found');
		}

		lesson.activity.forEach((activity: Activity, i: number) => {
			if (!lesson) {
				throw new Error('Lesson not found');
			}

			if (!lesson.activity || lesson.activity.length === 0) {
				throw new Error('Lesson has no activities');
			}

			if (activity.uuid === this.activity()?.uuid) {
				if (i < lesson.activity.length - 1) {
					this.nav.navigateWithMatrix$(['/', 'my', 'journey'], {
						activityUuid: lesson.activity[i + 1].uuid,
					});
				}
			}
		});
	}

	public async onUp(): Promise<void> {
		const matrixParams: { [key: string]: string } = this.nav.matrixParams$.getValue();

		if (this.isStrideCourse()) {
			this.nav.navigateWithMatrix$(
				['/', 'my', 'journey'],
				{
					courseUuid: matrixParams['courseUuid'],
					sectionUuid: matrixParams['sectionUuid'],
					locationUuid: matrixParams['locationUuid'],
				},
				false
			);
			return;
		}

		this.nav.navigateWithMatrix$(
			['/', 'my', 'journey'],
			{
				courseUuid: matrixParams['courseUuid'],
				sectionUuid: matrixParams['sectionUuid'],
				locationUuid: matrixParams['locationUuid'],
				unitUuid: matrixParams['unitUuid'],
				lessonUuid: matrixParams['lessonUuid'],
			},
			false
		);
	}

	private updateSteyByRoute(params: { [key: string]: string }): void {
		if (params['activityUuid']) {
			this.step.set(JourneyStep.PLAY_ACTIVITY);
		} else if (params['lessonUuid']) {
			this.step.set(JourneyStep.CHOOSE_ACTIVITY);
		} else {
			this.step.set(JourneyStep.CHOOSE_COURSE_AND_UNIT_AND_LESSON);
		}
	}

	public async onLessonSelected(lesson: Lesson): Promise<void> {
		if (this.isStrideCourse() === true && this.course()) {
			await this.userService.redirectToStrideCourse$(this.course()!);
		} else {
			this.nav.navigateWithMatrix$(['/', 'my', 'journey'], {
				lessonUuid: lesson.uuid,
				unitUuid: lesson.unit_uuid,
			});
		}
	}

	ngOnDestroy(): void {
		this.sub?.unsubscribe();
	}
}
